import { useState, useCallback, useRef, useEffect } from "react";
import { Checkbox } from "@chakra-ui/react";
import { FiClock } from "react-icons/fi";
import { cn } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import Modal from "newui/components/ui/Modal/Modal";
import { useMyScores, useSubmitScores } from "../../../../../hooks/usePanelScoringQueries";
import { useInitialEvaluationContext } from "../../../../../hooks/InitialEvaluationContext";
import { SupplierResponseTable } from "../../SupplierResponseTable";
import { ScoringModal } from "../../ScoringModal";
import { EvaluationClarificationsTable } from "../clarifications/EvaluationClarificationsTable";
import { InvitationBanner } from "./InvitationBanner";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { initialEvaluationSteps } from "guides/features/Evaluation";
import { SubmitConfirmation } from "./SubmitConfirmation";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";
import type { PanelMemberStatus } from "../../../../../types/panel.type";
import type { PanelSupplierScores, SupplierEvaluationSummary } from "../../../../../types/scoring.type";
import { EvaluationStepNumber, PanelMemberBlockedReason } from "newui/features/Rfx/Evaluation/types/evaluation.type";
import { EVALUATION_STEP_NUMBERS } from "newui/features/Rfx/Evaluation/constants/evaluation.constants";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";

interface PanelMemberScoringViewProps {
	currentStep: EvaluationStepNumber;
	deadline?: string | null;
	evalId: number;
	memberId: number;
	memberStatus: PanelMemberStatus;
	rfxData: Rfx;
	/** Server-side reason the member is blocked from scoring (e.g. a declared
	 * conflict of interest awaiting RFx Lead review). Undefined when not blocked. */
	scoringBlockedReason?: PanelMemberBlockedReason | null;
}

type TabId = "my-scoring" | "my-clarifications";

const mapPanelScoresToSuppliers = (
	suppliers: Array<PanelSupplierScores>
): SupplierEvaluationSummary[] => {
	return suppliers.map((s) => {
		const total = s.criteria.length;
		const scored = s.criteria.filter((c) => c.score !== null).length;
		const allDone = total > 0 && scored === total;
		return {
			inviteId: s.invite_id,
			supplierName: s.supplier_name,
			supplierLogo: s.supplier_logo,
			companyId: 0,
			status: allDone ? "done" as const : scored > 0 ? "in_progress" as const : "not_started" as const,
			percentComplete: total > 0 ? Math.round((scored / total) * 100) : 0,
			startedAt: s.started_at,
			completedAt: s.completed_at,
			finalStatus: null,
			finalPercentComplete: null,
			finalSubmittedAt: null,
		} as SupplierEvaluationSummary;
	});
};

const INITIAL_INSTRUCTIONS: React.ReactNode[] = [
	"Score each supplier against the evaluation criteria. Use the scoring guide for reference on how to assign scores.",
	"Add comments to explain your scoring rationale. Comments are visible to the RFx Lead.",
	"Your scores are saved automatically as you work. Submit when you have finished scoring all suppliers.",
	"Once submitted these initial scores are final and cannot be modified",
	"After submitting, please wait until all panel members have completed their evaluation. The RFx lead will notify you if a panel meeting is required, or of any next steps.",
];

const formatDeadline = (dateStr: string | null | undefined): string => {
	if (!dateStr) {
		return "No deadline set";
	}
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-AU", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

export const PanelMemberScoringView: React.FC<PanelMemberScoringViewProps> = ({
	currentStep,
	rfxData,
	evalId,
	memberId,
	memberStatus,
	deadline,
	scoringBlockedReason,
}) => {
	const rfxId = rfxData?.id;
	const [currentStatus, setCurrentStatus] = useState<PanelMemberStatus>(memberStatus);
	const [activeTab, setActiveTab] = useState<TabId>("my-scoring");
	const [selectedInviteId, setSelectedInviteId] = useState<number | null>(null);
	const [showDeclineDrawer, setShowDeclineDrawer] = useState(false);
	const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
	const [clarificationSupplierId, setClarificationSupplierId] = useState<number | null>(null);
	const [confirmChecked, setConfirmChecked] = useState(false);
	const pendingResolveRef = useRef<((value: boolean) => void) | null>(null);

	const canScore = currentStatus === "accepted" || currentStatus === "opened" || currentStatus === "in_progress";

	const { data: myScoresData, isLoading: isLoadingScores, refetch } = useMyScores(
		canScore ? rfxId : undefined,
		canScore ? evalId : undefined
	);

	const { suppliers: contextSuppliers } = useInitialEvaluationContext();
	const submitMutation = useSubmitScores(rfxId, evalId);

	const suppliers = myScoresData
		? mapPanelScoresToSuppliers(myScoresData.suppliers)
		: [];

	const allSuppliersScored = suppliers.length > 0 && suppliers.every((s) => s.status === "done");

	useEffect(() => {
		setCurrentStatus(memberStatus);
	}, [memberStatus]);

	const handleAccept = useCallback(() => {
		setCurrentStatus("accepted");
	}, []);

	const handleDeclined = useCallback(() => {
		setCurrentStatus("declined");
	}, []);

	const handleSelectSupplier = useCallback((inviteId: number) => {
		setSelectedInviteId(inviteId);
	}, []);

	const handleCloseModal = useCallback(() => {
		// Just close — `handleScoringComplete` already refetches when an
		// actual save occurs inside the modal. Refetching here as well
		// caused a redundant network round-trip on every close, which in
		// turn replaced supplier rows with freshly-signed logo URLs and
		// forced the browser to re-download every logo.
		setSelectedInviteId(null);
	}, []);

	const handleScoringComplete = useCallback(() => {
		refetch();
	}, [refetch]);

	const handleSubmitScores = useCallback(() => {
		if (!myScoresData) {
			return;
		}
		const scores = myScoresData.suppliers.flatMap((s) =>
			s.criteria.map((c) => ({
				rfx_criteria_id: c.rfx_criteria_id,
				rfx_invite_id: s.invite_id,
				score: c.score,
				comment: c.comment,
			}))
		);
		submitMutation.mutate(scores, {
			onSuccess: () => {
				setShowSubmitConfirm(false);
				setConfirmChecked(false);
				setCurrentStatus("submitted");
			},
		});
	}, [myScoresData, submitMutation]);

	const clarificationAvailable = currentStep <= EVALUATION_STEP_NUMBERS.CLARIFICATIONS;

	const handleAddClarification = useCallback((inviteId: number) => {
		setActiveTab("my-clarifications");
		setClarificationSupplierId(inviteId);
	}, []);

	const handleAutoOpenHandled = useCallback(() => {
		setClarificationSupplierId(null);
	}, []);

	const isSubmitted = currentStatus === "submitted";

	// Revoked state
	if (currentStatus === "revoked") {
		return (
			<div className="space-y-4" data-testid="panel-member-scoring-declined">
				<div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
					<p className="text-sm text-gray-600">
						{"Your access to this evaluation has been revoked - please contact the RFx Lead for more information."}
					</p>
				</div>
			</div>
		);
	}

	// Invited state — show banner
	if (currentStatus === "invited" || currentStatus === "pending" || currentStatus === "declined") {
		return null;
	}

	if (!rfxData) {
		return <LoadingSpinner />;
	}

	// Active or submitted scoring state — tabbed view
	return (
		<div className="space-y-4" data-testid={isSubmitted ? "panel-member-scoring-submitted" : "panel-member-scoring-active"}>

			{!isSubmitted && (
				<>
					<InstructionsInfoBox
						instructions={INITIAL_INSTRUCTIONS}
						guideSteps={initialEvaluationSteps}
						data-testid="instructions-info-box"
						footer={deadline ? (
							<span className="inline-flex items-center gap-1.5">
								<FiClock size={12} className="text-gray-400" aria-hidden="true" />
								Deadline: {formatDeadline(deadline)}
							</span>
						) : undefined}
					/>
				</>
			)}

			<div className="flex border-b border-gray-200" data-testid="member-tabs" role="tablist">
				<button
					type="button"
					role="tab"
					aria-selected={activeTab === "my-scoring"}
					aria-controls="tab-panel-my-scoring"
					onClick={() => setActiveTab("my-scoring")}
					className={cn(
						"px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
						activeTab === "my-scoring"
							? "border-[#0031DD] text-[#0031DD]"
							: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
					)}
					data-testid="tab-my-scoring"
				>
					My Scoring
				</button>
				<button
					type="button"
					role="tab"
					aria-selected={activeTab === "my-clarifications"}
					aria-controls="tab-panel-my-clarifications"
					onClick={() => setActiveTab("my-clarifications")}
					className={cn(
						"px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
						activeTab === "my-clarifications"
							? "border-[#0031DD] text-[#0031DD]"
							: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
					)}
					data-testid="tab-my-clarifications"
				>
					My Clarifications
				</button>
			</div>

			<div
				id="tab-panel-my-scoring"
				role="tabpanel"
				className={activeTab === "my-scoring" ? "space-y-4" : "hidden"}
			>
				{isSubmitted ? (
					<SubmitConfirmation
						rfxId={rfxId}
						evalId={evalId}
						submittedAt={myScoresData?.submitted_at}
						onAddClarification={clarificationAvailable ? handleAddClarification : undefined}
					/>
				) : isLoadingScores ? (
					<div className="space-y-3">
						{[1, 2, 3].map((i) => (
							<SkeletonBase key={i} startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
						))}
					</div>
				) : (
					<>
						<SupplierResponseTable
							suppliers={suppliers}
							isLoading={false}
							rfxId={rfxId}
							onSelectSupplier={handleSelectSupplier}
						/>

						{allSuppliersScored && (
							<div className="flex justify-end">
								<button
									type="button"
									onClick={() => setShowSubmitConfirm(true)}
									disabled={submitMutation.isPending}
									className="inline-flex items-center gap-2 rounded-lg bg-[#0031DD] px-4 py-2 text-sm font-medium text-white hover:bg-[#0028b8] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0031DD] focus-visible:ring-offset-2"
									data-testid="submit-scores-btn"
								>
									{submitMutation.isPending ? "Submitting..." : "Submit Scores"}
								</button>
							</div>
						)}
					</>
				)}
			</div>

			<div
				id="tab-panel-my-clarifications"
				role="tabpanel"
				className={activeTab === "my-clarifications" ? "space-y-4" : "hidden"}
			>
				{contextSuppliers.length > 0 && (
					<EvaluationClarificationsTable
						rfxId={rfxId}
						evalId={evalId}
						suppliers={contextSuppliers.map((s) => ({
							inviteId: s.inviteId,
							supplierName: s.supplierName,
							supplierLogo: s.supplierLogo,
						}))}
						autoOpenForSupplierId={clarificationSupplierId}
						onAutoOpenHandled={handleAutoOpenHandled}
					/>
				)}
			</div>

			{!isSubmitted && selectedInviteId !== null && (
				<ScoringModal
					isOpen={selectedInviteId !== null}
					rfxId={rfxId}
					evalId={evalId}
					suppliers={suppliers}
					selectedInviteId={selectedInviteId}
					onClose={handleCloseModal}
					onSupplierChange={handleSelectSupplier}
					onScoringComplete={handleScoringComplete}
				/>
			)}

			{!isSubmitted && showSubmitConfirm && (
				<Modal
					isOpen={showSubmitConfirm}
					onClose={() => {
						setShowSubmitConfirm(false);
						setConfirmChecked(false);
					}}
					title="Submit Scores"
					size="md"
					iconType="warning"
					modalType="standard"
					closeOnEsc
					closeOnOverlayClick={false}
					primaryButton={{
						text: submitMutation.isPending ? "Submitting..." : "Submit Scores",
						onClick: handleSubmitScores,
						disabled: !confirmChecked || submitMutation.isPending,
						width: "200px"
					}}
					secondaryButton={{
						text: "Cancel",
						onClick: () => {
							setShowSubmitConfirm(false);
							setConfirmChecked(false);
						},
					}}
				>
					<div className="space-y-4">
						<p className="text-sm text-gray-600 text-center">
							Once submitted, your scores and comments cannot be edited.
							Please ensure all scores are accurate before proceeding.
						</p>
						<label
							className="flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 p-3 hover:bg-gray-50 transition-colors"
							data-testid="submit-confirm-checkbox"
						>
							<Checkbox
								isChecked={confirmChecked}
								onChange={(e) => setConfirmChecked(e.target.checked)}
								colorScheme="blue"
								mt={0.5}
							/>
							<span className="text-sm text-gray-700">
								I confirm that I have completed all evaluations and understand
								that scores cannot be changed after submission.
							</span>
						</label>
					</div>
				</Modal>
			)}
		</div>
	);
};
